import CustomLoading from '@/components/custom-loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GiftCardTransactionsFragment } from '@/gql/generated';
import { ErrorType } from '@/lib/handle-error';
import { formatDateToTimezone } from '@/lib/timezone';
import { formatNumberToCurrency } from '@/lib/utils';
import { transactionsType } from '@/utils/constant';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import NoTransaction from '../../_components/no-transaction';

const TransactionTable = (props: {
  whmcsId: number | undefined;
  giftCardId: string;
  timezone: string;
  queryResult:
    | UseInfiniteQueryResult<GiftCardTransactionsFragment[], ErrorType>
    | undefined;
}) => {
  if (!props.queryResult) {
    return <NoTransaction />;
  }
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const bottom =
      e.currentTarget.scrollHeight ===
      e.currentTarget.scrollTop + e.currentTarget.clientHeight;
    if (
      bottom &&
      props.queryResult?.hasNextPage &&
      !props.queryResult.isFetchingNextPage
    ) {
      props.queryResult.fetchNextPage();
    }
  };
  return (
    <div
      className="overflow-auto h-full relative border-t border-t-gray-border"
      onScroll={handleScroll}
    >
      <Table>
        <TableHeader className="sticky -top-1 z-10 h-16 w-full bg-gray-lightest ring-1 ring-offset-0 ring-gray-border">
          <TableRow className="text-xl text-gray-primary border-gray-border">
            <TableHead className="font-bold">Date</TableHead>
            <TableHead className="font-bold">Order ID</TableHead>
            <TableHead className="font-bold">Amount</TableHead>
            <TableHead className="font-bold">Type</TableHead>
            <TableHead className="font-bold">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="text-2xl text-gray-primary cursor-pointer max-h-28">
          {props.queryResult.data?.map((item) => {
            const isAddFund = item.type === transactionsType.TOP_UP;
            return (
              <TableRow key={item.id} className="border-gray-border">
                <TableCell>
                  <div className="text-2xl font-normal py-2">
                    <div className="inline-block">
                      {formatDateToTimezone(
                        item.date,
                        props.timezone,
                        'MM/dd/yy',
                      )}
                    </div>{' '}
                    <div className="inline-block">
                      {formatDateToTimezone(
                        item.date,
                        props.timezone,
                        'hh:mm a',
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{item?.code}</TableCell>
                <TableCell
                  className={clsx(
                    'text-2xl font-bold',
                    isAddFund ? 'text-success' : 'text-danger-discount',
                  )}
                >
                  {isAddFund ? '+' : '-'}
                  {formatNumberToCurrency(item.balance_change)}
                </TableCell>
                <TableCell>
                  <span className="text-2xl text-gray-primary">
                    {isAddFund ? 'Add funds' : 'Redeem'}
                  </span>
                </TableCell>
                <TableCell className="font-bold">
                  {formatNumberToCurrency(item.balance_after)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {props.queryResult.isFetchingNextPage && (
        <div className="flex items-center justify-center">
          <CustomLoading />
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
